import { isNonEmpty } from '@sgme/fp';
import classNames from 'classnames';
import {
  CollapsableTable,
  CollapsableTableTrigger,
} from 'components/common-form/builder/form/node/field/DealSplit/CollapsableTable/CollapsableTable';
import type {
  CollapsableTableColumn,
  CollapsableTableOptions,
} from 'components/common-form/builder/form/node/field/DealSplit/CollapsableTable/CollapsableTable.model';
import { DEFAULT_COLLAPSABLE_GROUP_ID } from 'components/common-form/builder/form/node/field/DealSplit/CollapsableTable/CollapsableTable.model';
import { DealSplitIAMainCell } from 'components/common-form/builder/form/node/field/DealSplit/DealSplitIAMainCell';
import { DealSplitIAValueDateCell } from 'components/common-form/builder/form/node/field/DealSplit/DealSplitIAValueDateCell';
import { useGridPatchedCells } from 'components/common-form/hooks/patched-fields/useGridPatchedCells';
import { AmountWithUnit } from 'components/shared/Amount/Amount';
import { FormattedMessage } from 'components/shared/Intl';
import type { DealSplitIndependentAmount } from 'models/forms/DealSplitData';
import { FC, useContext, useMemo } from 'react';
import { logAnalytics } from 'shared/middlewares/analytics/logger';
import { WorkspaceAnalyticCategoryContext } from 'shared/middlewares/analytics/loggerContext';

import { AddRowButton } from './AddRowButton';
import type { DealSplitRowModel } from './types';
import { MaxSizedUnallocatedAmount } from './UnallocatedAmount';
import { hasIndependentAmountValues } from './utils';

export interface DealSplitGridProps {
  className?: string;
  fieldId: string;
  currency: string;
  dealSplits: DealSplitRowModel[];
  tradeDate?: Nullable<string>;
  isReadonly?: boolean;
  hasError?: boolean;
  notionalToSplit?: number;
  unallocatedNotional: number;
  canAddRow: boolean;
  canDeleteRow: boolean;
  hasIndependentAmount?: boolean;
  onRowAdded: () => void;
  onRowDeleted: (rowIndex: number) => void;
  onRowCustodianChanged: (rowIndex: number, custodianId: string) => void;
  onRowNotionalChanged: (rowIndex: number, notional?: number) => void;
  onRowIndependentAmountChanged?: (rowIndex: number, independentAmount?: DealSplitIndependentAmount) => void;
  tabIndex?: number;
}

export const DealSplitGrid: FC<DealSplitGridProps> = ({
  className,
  fieldId,
  currency,
  tradeDate,
  canAddRow,
  canDeleteRow,
  hasIndependentAmount,
  dealSplits,
  hasError,
  isReadonly,
  notionalToSplit,
  unallocatedNotional,
  onRowAdded,
  onRowDeleted,
  onRowCustodianChanged,
  onRowNotionalChanged,
  onRowIndependentAmountChanged,
  tabIndex,
}) => {
  const logAnalyticsCategory = useContext(WorkspaceAnalyticCategoryContext);
  const getCellHighlight = useGridPatchedCells(fieldId);

  const hasAnyIndependentAmount =
    !!hasIndependentAmount &&
    dealSplits?.some(({ independentAmount }) => hasIndependentAmountValues(independentAmount));

  const columns: CollapsableTableColumn<DealSplitRowModel>[] = useMemo(() => {
    const baseColumns: CollapsableTableColumn<DealSplitRowModel>[] = [
      {
        id: 'custodian',
        header: <FormattedMessage id="custodian" />,
        cellClassName: ({ rowIndex }) => getCellHighlight(rowIndex, 'Custodian'),
        renderCell: ({ row, rowIndex }) => (
          <select
            id={`DealSplit-Custodian-${rowIndex}`}
            data-e2e={`DealSplit-Custodian-${rowIndex}-select`}
            className="form-select form-control-md"
            value={row.custodianId || ''}
            disabled={isReadonly}
            onChange={event => {
              const newValue = event.target.value;
              if (newValue !== row.custodianId) {
                onRowCustodianChanged(rowIndex, newValue);
              }
            }}
            tabIndex={tabIndex}
          >
            <option value="" disabled />
            {row.availableCustodians.map(({ id, label, eliotCode }) => (
              <option value={id} key={id}>
                {isNonEmpty(eliotCode) ? `${label} (${eliotCode})` : label}
              </option>
            ))}
          </select>
        ),
      },
      {
        id: 'notional',
        header: <FormattedMessage id="notional" />,
        cellClassName: ({ rowIndex }) =>
          classNames('d-flex flex-row', getCellHighlight(rowIndex, 'NotionalAmountInGrid')),
        renderCell: ({ row, rowIndex }) => (
          <AmountWithUnit
            id={`DealSplit-NotionalAmountInGrid-${rowIndex}`}
            e2e={`DealSplit-NotionalAmountInGrid-${rowIndex}`}
            className="flex-grow-1"
            unit={currency}
            value={row.notional}
            isDisabled={isReadonly || !(notionalToSplit && row.custodianId)}
            onBlur={value => {
              if (value !== row.notional) {
                onRowNotionalChanged(rowIndex, value);
              }
            }}
            tabIndex={tabIndex}
          />
        ),
      },
    ];

    if (hasIndependentAmount) {
      baseColumns.push(
        {
          id: 'ia-main',
          header: (
            <CollapsableTableTrigger>
              {({ isExpanded }) => (
                <FormattedMessage
                  id={
                    !isExpanded
                      ? 'form.DealSplit.independentAmount.shortLabel'
                      : 'form.DealSplit.independentAmount.title'
                  }
                />
              )}
            </CollapsableTableTrigger>
          ),
          cellClassName: ({ rowIndex }) => classNames('align-middle', getCellHighlight(rowIndex, 'IndependentAmount')),
          renderCell: ({ row, rowIndex }) => (
            <DealSplitIAMainCell
              dealSplits={dealSplits}
              row={row}
              rowIndex={rowIndex}
              currency={currency}
              isReadonly={isReadonly}
              onRowIndependentAmountChanged={onRowIndependentAmountChanged}
            />
          ),
        },
        {
          id: 'ia-value-date',
          isCollapsable: true,
          header: <FormattedMessage id="form.DealSplit.independentAmount.valueDate" />,
          cellClassName: ({ rowIndex }) =>
            classNames('align-middle ia-value-date-cell', getCellHighlight(rowIndex, 'IndependentAmountValueDate')),
          renderCell: ({ row, rowIndex }) => (
            <DealSplitIAValueDateCell
              row={row}
              rowIndex={rowIndex}
              tradeDate={tradeDate}
              isReadonly={isReadonly}
              onRowIndependentAmountChanged={onRowIndependentAmountChanged}
            />
          ),
        },
      );
    }

    if (!isReadonly && canDeleteRow) {
      baseColumns.push({
        id: 'delete',
        header: <FormattedMessage id="form.DealSplit.independentAmount.delete" />,
        cellClassName: 'align-middle text-center px-1',
        renderCell: ({ rowIndex }) => (
          <button
            className="btn btn-icon"
            disabled={isReadonly}
            onClick={() => {
              if (!isReadonly) {
                logAnalytics.trackEvent(logAnalyticsCategory, 'Remove custodian');
                onRowDeleted(rowIndex);
              }
            }}
          >
            <i className="clickable icon text-secondary">delete</i>
          </button>
        ),
      });
    }

    return baseColumns;
  }, [
    canDeleteRow,
    currency,
    hasIndependentAmount,
    isReadonly,
    logAnalyticsCategory,
    getCellHighlight,
    dealSplits,
    notionalToSplit,
    onRowCustodianChanged,
    onRowDeleted,
    onRowIndependentAmountChanged,
    onRowNotionalChanged,
    tabIndex,
    tradeDate,
  ]);

  const collapsableTableOptions: CollapsableTableOptions<DealSplitRowModel> = {
    rows: dealSplits,
    columns,
    getRowKey: (_, index) => index,
    initialExpandedByGroup: {
      [DEFAULT_COLLAPSABLE_GROUP_ID]: hasAnyIndependentAmount,
    },
    tableClassName: 'collapsable-table',
    tableE2E: 'deal-splits-table',
  };

  return (
    <div
      data-e2e="deal-splits-container"
      className={classNames('dealsplits', className, { 'border-top border-bottom border-warning': hasError })}
    >
      <CollapsableTable options={collapsableTableOptions} />

      <div className="mt-3 d-flex w-100">
        {!isReadonly && canAddRow && (
          <AddRowButton
            onClick={() => {
              logAnalytics.trackEvent(logAnalyticsCategory, 'Add custodian');
              onRowAdded();
            }}
          />
        )}

        <span className="flex-grow-1" />
        <MaxSizedUnallocatedAmount
          className="px-0 me-1"
          amount={unallocatedNotional}
          currency={currency}
          hasWarning={unallocatedNotional !== 0}
          isDisabled={isReadonly ?? false}
          tabIndex={tabIndex}
        />
      </div>
    </div>
  );
};
